import { useCallback, useEffect, useRef } from 'react';
import { AnomalyQueuePanel } from './AnomalyQueuePanel';
import { AssistQueueList } from './AssistQueueList';
import { NewsSocialFeed } from './NewsSocialFeed';
import { PinnedMediaRegion } from './PinnedMediaRegion';
import { PanelQueueColumn } from './PanelQueueColumn';
import { PanelRegionTabs } from './PanelRegionTabs';
import { StackedQueueTabs } from './StackedQueueTabs';
import { usePinnedFeeds } from './pinnedFeeds';
import { announcePanel } from './panelBridge';
import { broadcastSelection } from './selectionBridge';
import { useElementSize } from './lib/useElementSize';
import { useApiHealth } from './lib/useApiHealth';
import { useQueueSignals } from './lib/useQueueSignals';
import { queueLayoutForWidth } from './lib/panelQueueLayout';
import { dockModeForSize } from './lib/panelDock';
import { CANADA_BBOX } from './api';

/**
 * Operations panel (`/panel`, #197 / control-panel spec §0, §2).
 *
 * A second-screen / dual-monitor surface, served same-origin like `?wall=1` and
 * `/popout`. It composes three regions under the panel's content law:
 *   • PROTECTED queues — the Urgent Alert queue + ASSIST queue get reserved space
 *     that media can never compress (spec §2). They live in their own flex region
 *     (a fixed-width column in landscape, a top stack in portrait); the media
 *     region is a separate sibling, so a flood of pins can't steal queue space.
 *   • BOUNDED + ADAPTIVE media region — pinned cams/news fill the region's
 *     reserved area (1=full · 2=split-to-fill · 3-4=grid · 5+=grid+scroll),
 *     never cropping and never compressing the queues (`PinnedMediaRegion`, #208).
 *   • Orientation-aware — landscape is the interactive 1/2/grid layout; portrait
 *     is a single-column media reel under the reading/list queues (spec §0).
 *
 * Dock decision — MEASURED, not a device guess (#186, extends #131). The macro
 * dock (landscape map/grid vs portrait reading/list) is chosen from the panel
 * surface's OWN measured aspect ratio (`dockModeForSize`: portrait when height ≥
 * width), driven by the SAME ResizeObserver as the queue sub-layout — not a
 * `matchMedia('(orientation: portrait)')` viewport/device guess. So a portrait
 * monitor (the PEOC seat) auto-docks to the reading/list layout, and the dock is
 * correct for any window or embedding, not only a maximized top-level window.
 *
 * Portrait queue layout — WIDTH-ADAPTIVE (#211, spec §2). The arrangement is
 * driven by the panel's MEASURED width (a ResizeObserver), not a device/portrait
 * guess, so it adapts to any window:
 *   • WIDE enough for two readable columns → the two protected queues sit
 *     SIDE-BY-SIDE (two columns); each pulses its header on an update
 *     (`PanelQueueColumn` + the `useQueueSignals` counters).
 *   • TOO NARROW → the stacked-tabs fallback (`StackedQueueTabs`): one full-width
 *     queue at a time + a blink on the hidden tab so an update is never missed.
 * The switch is clean across the threshold (`queueLayoutForWidth`).
 *
 * Portrait height fit (#211, tightened #204, feed-fit #214-followup): the panel is
 * pinned to the visual viewport (`100dvh` + `overflow-hidden`) so it never page-
 * scrolls — critical on a tablet/phone where the browser chrome makes `100%` taller
 * than the screen and buries the 2nd feed. The queue block is a bounded flex-basis
 * (`--panel-queue-h`, grow 0 / shrink 1) that yields its height FIRST; the media
 * reel (`flex-1`) takes the leftover and sizes each feed to its NATURAL ~16:9
 * height, showing as many as FIT usably and scrolling the rest — never two thin
 * slivers (supersedes #214's forced 50/50 split; see `PinnedMediaRegion`).
 *
 * Empty state — 0 pins (PART 1, #214-followup): the flex flips. The queues GROW
 * (`flex: 1 1 auto`) to fill almost the whole panel and the media region collapses
 * to a slim bottom strip with a compact "+ Add feed" affordance — no big empty
 * media box. (≥1 pin restores the bounded-queue + media-bulk layout above.)
 *
 * Sync: it announces its presence (two-queue guard — the COP hides its own queue
 * while a panel is open) and drives the COP by broadcasting a selection when a
 * queue row is clicked (the selection bridge). Layer toggles stay on the COP
 * (spec §2) — there are none here, and no map.
 */

function makePanelId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `panel-${Math.random().toString(36).slice(2)}`;
}

export function PanelView(): JSX.Element {
  const { pins, unpin } = usePinnedFeeds();
  const hasPins = pins.length > 0;
  const queueSignals = useQueueSignals();

  // Measure the panel's live size with ONE ResizeObserver and drive BOTH layout
  // decisions off it — no `matchMedia`/device guess (#186, extends #211):
  //   • the macro DOCK — portrait reading/list vs landscape map/grid — from the
  //     surface's aspect ratio (`dockModeForSize`), so a portrait monitor (taller
  //     than wide) auto-docks to the reading/list layout;
  //   • the portrait queue sub-layout (`queueLayoutForWidth`) — side-by-side when
  //     two readable columns fit, stacked-tabs when not.
  const [rootRef, { width: panelW, height: panelH }] = useElementSize<HTMLDivElement>();
  const orientation = dockModeForSize(panelW, panelH);
  const queueMode = queueLayoutForWidth(panelW);

  // Announce presence for the lifetime of this window (two-queue guard).
  const panelId = useRef<string>(makePanelId()).current;
  useEffect(() => announcePanel(panelId), [panelId]);

  // #325 — the ops panel is its own same-origin root, so it polls /api/health
  // independently: a total outage must not read as a quiet all-clear HERE either. This
  // instance doesn't badge (no onCountChange), but `reachable` still drives its
  // "Alerts unavailable — cannot reach live data" empty-state.
  const apiHealth = useApiHealth();

  // Queue-row click → hand the selection to the COP (one-way bridge); the COP
  // opens + flies. onFlyTo is a no-op here — the COP does the flying.
  const noFly = useCallback(() => undefined, []);
  const queue = (
    <AnomalyQueuePanel
      bbox={CANADA_BBOX}
      visible
      capVisible
      selectedId={null}
      refreshNonce={0}
      reachable={apiHealth.reachable}
      onSelectHotspot={(h) =>
        broadcastSelection({
          kind: 'hotspot',
          id: h.properties.id,
          lon: h.geometry.coordinates[0],
          lat: h.geometry.coordinates[1],
        })
      }
      onSelectCap={(c) => broadcastSelection({ kind: 'cap', id: c.properties.id })}
      onSelectSmoke={(s) =>
        broadcastSelection({
          kind: 'smoke',
          id: s.properties.id,
          lon: s.geometry.coordinates[0],
          lat: s.geometry.coordinates[1],
        })
      }
      onSelectIce={(i) =>
        broadcastSelection({
          kind: 'ice',
          id: i.properties.id,
          lon: i.geometry.coordinates[0],
          lat: i.geometry.coordinates[1],
        })
      }
      onSelectFlood={(f) =>
        broadcastSelection({
          kind: 'flood',
          id: f.properties.id,
          lon: f.geometry.coordinates[0],
          lat: f.geometry.coordinates[1],
        })
      }
      onSelectMediaSignal={(m) =>
        broadcastSelection({
          kind: 'media',
          id: m.properties.id,
          lon: m.geometry.coordinates[0],
          lat: m.geometry.coordinates[1],
        })
      }
      onSelectOcFire={(f) =>
        broadcastSelection({
          kind: 'fire',
          id: f.properties.id,
          lon: f.properties.centroid[0],
          lat: f.properties.centroid[1],
        })
      }
      onFlyTo={noFly}
    />
  );
  const assist = <AssistQueueList />;
  // The News & Social wire surface (#65/#183 wave 3b) — a tab BEHIND the
  // protected queues (PanelRegionTabs): same reserved region, queues default,
  // hidden-Queues-tab blink so an alert update is never missed.
  const news = <NewsSocialFeed surface="panel" />;

  return (
    <div
      ref={rootRef}
      // Pinned to the VISUAL viewport with `100dvh` (not `h-full`/`100%`): on a
      // tablet/phone in portrait the dynamic browser chrome makes `100%` taller
      // than what's actually on screen, pushing the 2nd feed below the fold (the
      // #211 follow-up). `overflow-hidden` guarantees the panel itself never page-
      // scrolls — the queue scrolls inside its own bounded box instead.
      className="flex h-[100dvh] w-full flex-col overflow-hidden bg-[#0b1020] text-slate-100"
      data-testid="panel-view"
      data-orientation={orientation}
      data-queue-mode={orientation === 'portrait' ? queueMode : undefined}
    >
      <header className="flex shrink-0 items-center justify-between border-b border-slate-700 px-4 py-2">
        <p className="text-sm font-bold tracking-tight">TSR · Operations Panel</p>
        <p className="text-[11px] text-slate-400">{orientation} · synced to COP</p>
      </header>

      {orientation === 'landscape' ? (
        <div className="flex min-h-0 flex-1">
          {/* PROTECTED queue column — fixed width, never shrinks for media. */}
          <div
            data-testid="queue-region"
            className="flex w-[var(--panel-queue-w)] shrink-0 flex-col border-r border-slate-700 p-3"
          >
            <PanelRegionTabs
              signals={queueSignals}
              queues={
                <div className="flex h-full min-h-0 flex-col gap-3">
                  <div className="min-h-0 flex-1 overflow-hidden rounded-lg border border-slate-700">
                    {queue}
                  </div>
                  <div className="h-[var(--panel-assist-h)] shrink-0">{assist}</div>
                </div>
              }
              news={news}
            />
          </div>
          {/* BOUNDED media region — separate sibling, capped at 4 tiles. */}
          <div className="min-w-0 flex-1 overflow-hidden p-3">
            <PinnedMediaRegion pins={pins} orientation="landscape" onUnpin={unpin} />
          </div>
        </div>
      ) : (
        <div className="flex min-h-0 flex-1 flex-col">
          {/* PROTECTED queues at the top — WIDTH-ADAPTIVE (#211). The region is a
              bounded, SCROLLABLE flex-basis (`--panel-queue-h`, grow 0 / shrink 1):
              on a tall window it sits at that height; as the window shrinks it gives
              up height FIRST so the media reel keeps its two-feed reservation. The
              ARRANGEMENT switches on measured width — side-by-side columns when two
              readable columns fit, else the stacked-tabs fallback. */}
          <div
            data-testid="queue-region"
            data-mode={queueMode}
            data-fill={hasPins ? undefined : 'true'}
            className="min-h-0 overflow-hidden p-3"
            // With pins: bounded flex-basis that shrinks first so the media reel
            // keeps its two-feed reservation (#211). With NO pins: the queues GROW
            // to fill almost the whole panel (PART 1, spec §2) while the media
            // region collapses to a slim add-feed strip below.
            style={{ flex: hasPins ? '0 1 var(--panel-queue-h)' : '1 1 auto' }}
          >
            <PanelRegionTabs
              signals={queueSignals}
              queues={
                queueMode === 'side-by-side' ? (
                  <div className="flex h-full gap-3">
                    <PanelQueueColumn tab="alert" count={queueSignals.alert}>
                      {queue}
                    </PanelQueueColumn>
                    <PanelQueueColumn tab="assist" count={queueSignals.assist}>
                      {assist}
                    </PanelQueueColumn>
                  </div>
                ) : (
                  <StackedQueueTabs alert={queue} assist={assist} signals={queueSignals} />
                )
              }
              news={news}
            />
          </div>
          {/* BOUNDED media reel below — `flex-1` takes the leftover height after the
              bounded queue block, and the two feeds SPLIT that leftover (≈ half each,
              via the portrait reel solver) so both are fully visible. NO hard
              min-height: a fixed reservation (the old `--panel-media-min-h`) overflowed
              the viewport on short windows and pushed the 2nd feed below the fold; with
              the root pinned to `100dvh` + `overflow-hidden`, the queue (grow 0) yields
              its space first and the media reel always fits on-screen (spec §2). A 5th
              pin scrolls within the reel. */}
          <div
            data-testid="media-shell"
            className={
              hasPins
                ? 'min-h-0 flex-1 border-t border-slate-700 p-3'
                : // Empty: a slim bottom strip (shrink-0, content height) holding
                  // the compact add-feed affordance — no big empty media box.
                  'shrink-0 border-t border-slate-700 px-3 py-1.5'
            }
          >
            <PinnedMediaRegion pins={pins} orientation="portrait" onUnpin={unpin} />
          </div>
        </div>
      )}
    </div>
  );
}
